/**
 * Keyboard Phrases Configuration Types and Storage
 * 
 * Handles customization of quick phrases shown in AAC Keyboard phrases mode
 * Supports gender-aware phrases for Hebrew (masculine/feminine forms)
 */

import { getUserSettings } from "@/lib/userSettings";

export interface KeyboardPhrase {
  id: string;
  textHe: string;
  textEn: string;
  /** Hebrew text for female gender (optional - uses textHe if not provided) */
  textHeFemale?: string;
  /** TTS text for male Hebrew (optional - for words with same spelling but different pronunciation) */
  ttsHe?: string;
  /** TTS text for female Hebrew (optional - for words with same spelling but different pronunciation) */
  ttsHeFemale?: string;
  order: number;
  enabled: boolean;
}

export interface KeyboardPhrasesConfig {
  phrases: KeyboardPhrase[];
  updatedAt: string;
}

// Default phrases with gender-aware Hebrew text
// textHe = masculine form, textHeFemale = feminine form
// ttsHe/ttsHeFemale = phonetic text for TTS when pronunciation differs from spelling
const DEFAULT_PHRASES: KeyboardPhrase[] = [
  { id: "phrase-1", textHe: "אני בסדר", textEn: "I'm okay", order: 0, enabled: true },
  { id: "phrase-2", textHe: "בוא אליי", textEn: "Come to me", order: 1, enabled: true },
  { id: "phrase-3", textHe: "צריך עזרה", textHeFemale: "צריכה עזרה", textEn: "Need help", order: 2, enabled: true },
  { id: "phrase-4", textHe: "תתקשר אליי", textEn: "Call me", order: 3, enabled: true },
  { id: "phrase-5", textHe: "אני אוהב אותך", textHeFemale: "אני אוהבת אותך", textEn: "I love you", order: 4, enabled: true },
  { id: "phrase-6", textHe: "תודה", textEn: "Thank you", order: 5, enabled: true },
  { id: "phrase-7", textHe: "מתי תבוא?", textEn: "When are you coming?", order: 6, enabled: true },
  // "אני מחכה" - same spelling for both genders but different pronunciation
  // Male: מְחַכֶּה (me-cha-KE), Female: מְחַכָּה (me-cha-KA)
  { id: "phrase-8", textHe: "אני מחכה", textHeFemale: "אני מחכה", ttsHe: "אני מחכֶה", ttsHeFemale: "אני מחכָה", textEn: "I'm waiting", order: 7, enabled: true },
  { id: "phrase-9", textHe: "הכל טוב", textEn: "Everything is fine", order: 8, enabled: true },
  { id: "phrase-10", textHe: "אני צריך משהו", textHeFemale: "אני צריכה משהו", textEn: "I need something", order: 9, enabled: true },
  { id: "phrase-11", textHe: "אני עייף", textHeFemale: "אני עייפה", textEn: "I'm tired", order: 10, enabled: true },
  { id: "phrase-12", textHe: "אני רעב", textHeFemale: "אני רעבה", textEn: "I'm hungry", order: 11, enabled: true },
];

const STORAGE_KEY = "keyboardPhrases_v3"; // v3: added TTS-specific text for pronunciation

// Generate unique ID
function generateId(): string {
  return `phrase-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Load phrases config from localStorage
export function loadPhrasesConfig(): KeyboardPhrasesConfig {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const config: KeyboardPhrasesConfig = JSON.parse(saved);
      return config;
    }
  } catch (e) {
    console.error("Failed to load keyboard phrases config:", e);
  }
  
  // Return default
  return {
    phrases: [...DEFAULT_PHRASES],
    updatedAt: new Date().toISOString(),
  };
}

// Save phrases config to localStorage
export function savePhrasesConfig(config: KeyboardPhrasesConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error("Failed to save keyboard phrases config:", e);
  }
}

// Add a new phrase (empty by default - user fills in)
export function addPhrase(
  config: KeyboardPhrasesConfig
): { config: KeyboardPhrasesConfig; newPhraseId: string } {
  const newPhraseId = generateId();
  const newPhrase: KeyboardPhrase = {
    id: newPhraseId,
    textHe: "",
    textEn: "",
    order: config.phrases.length,
    enabled: true,
  };
  
  return {
    config: {
      ...config,
      phrases: [...config.phrases, newPhrase],
      updatedAt: new Date().toISOString(),
    },
    newPhraseId,
  };
}

// Update an existing phrase
export function updatePhrase(
  config: KeyboardPhrasesConfig,
  phraseId: string,
  updates: Partial<Omit<KeyboardPhrase, 'id'>>
): KeyboardPhrasesConfig {
  return {
    ...config,
    phrases: config.phrases.map(phrase =>
      phrase.id === phraseId ? { ...phrase, ...updates } : phrase
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Delete a phrase
export function deletePhrase(
  config: KeyboardPhrasesConfig,
  phraseId: string
): KeyboardPhrasesConfig {
  const filteredPhrases = config.phrases.filter(p => p.id !== phraseId);
  
  // Reorder remaining phrases
  filteredPhrases.forEach((phrase, idx) => {
    phrase.order = idx;
  });
  
  return {
    ...config,
    phrases: filteredPhrases,
    updatedAt: new Date().toISOString(),
  };
}

// Reorder phrases (move from fromIndex to toIndex)
export function reorderPhrases(
  config: KeyboardPhrasesConfig,
  fromIndex: number,
  toIndex: number
): KeyboardPhrasesConfig {
  const newPhrases = [...config.phrases];
  const [moved] = newPhrases.splice(fromIndex, 1);
  newPhrases.splice(toIndex, 0, moved);
  
  // Update order property
  newPhrases.forEach((phrase, idx) => {
    phrase.order = idx;
  });
  
  return {
    ...config,
    phrases: newPhrases,
    updatedAt: new Date().toISOString(),
  };
}

// Reset to default phrases
export function resetPhrasesConfig(): KeyboardPhrasesConfig {
  const config: KeyboardPhrasesConfig = {
    phrases: [...DEFAULT_PHRASES],
    updatedAt: new Date().toISOString(),
  };
  savePhrasesConfig(config);
  return config;
}

// Get enabled phrases sorted by order (for use in AACKeyboard)
// Filters out empty phrases - they won't appear in the keyboard
export function getEnabledPhrases(config: KeyboardPhrasesConfig): KeyboardPhrase[] {
  return config.phrases
    .filter(p => p.enabled && (p.textHe.trim() !== "" || p.textEn.trim() !== ""))
    .sort((a, b) => a.order - b.order);
}

// Get default phrases for reference
export function getDefaultPhrases(): KeyboardPhrase[] {
  return [...DEFAULT_PHRASES];
}

/**
 * Get the gender-appropriate Hebrew text for a phrase (for display)
 * Uses user's gender setting from userSettings
 */
export function getGenderedPhraseText(phrase: KeyboardPhrase, language: 'he' | 'en' | 'ar' | 'ru'): string {
  // For non-Hebrew languages, return the English text (or Hebrew as fallback)
  if (language !== 'he') {
    return phrase.textEn || phrase.textHe;
  }
  
  // For Hebrew, check user's gender setting
  const settings = getUserSettings();
  const isFemale = settings.gender === 'female';
  
  // Return feminine form if available and user is female, otherwise masculine
  if (isFemale && phrase.textHeFemale) {
    return phrase.textHeFemale;
  }
  
  return phrase.textHe;
}

/**
 * Get the gender-appropriate TTS text for a phrase (for speech synthesis)
 * Some Hebrew words have the same spelling but different pronunciation for different genders
 * Uses user's gender setting from userSettings
 */
export function getGenderedPhraseTtsText(phrase: KeyboardPhrase, language: 'he' | 'en' | 'ar' | 'ru'): string {
  // For non-Hebrew languages, return the English text (or Hebrew as fallback)
  if (language !== 'he') {
    return phrase.textEn || phrase.textHe;
  }
  
  // For Hebrew, check user's gender setting
  const settings = getUserSettings();
  const isFemale = settings.gender === 'female';
  
  // Return TTS-specific text if available, otherwise fall back to display text
  if (isFemale) {
    // Female: prefer ttsHeFemale > textHeFemale > ttsHe > textHe
    return phrase.ttsHeFemale || phrase.textHeFemale || phrase.ttsHe || phrase.textHe;
  }
  
  // Male: prefer ttsHe > textHe
  return phrase.ttsHe || phrase.textHe;
}
